import { useQuery, useQueryClient } from '@tanstack/react-query';
import { conversationService } from '../../../services/conversationService';

export interface UseConversationsQueryOptions {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  enabled?: boolean;
}

export const useConversationsQuery = (options: UseConversationsQueryOptions = {}) => {
  const {
    page = 1,
    limit = 50,
    search = '',
    status = '',
    enabled = true
  } = options;

  return useQuery({
    queryKey: ['conversations', { page, limit, search, status }],
    queryFn: async () => {
      const filters = {
        page,
        limit,
        ...(search && { search }),
        ...(status && { status })
      };
      
      return await conversationService.getConversations(filters);
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (error && 'status' in error && error.status === 401) {
        return false;
      }
      return failureCount < 2;
    }
  });
};

export const useConversationStatsQuery = () => {
  return useQuery({
    queryKey: ['conversation-stats'],
    queryFn: () => conversationService.getConversationStats(),
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 2
  });
};

export const useConversationQuery = (conversationId: string | undefined) => {
  return useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => conversationService.getRevolutionaryConversation(conversationId!),
    enabled: !!conversationId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 2
  });
};

// Utility hook to prefetch conversations
export const useConversationsPrefetch = () => {
  const queryClient = useQueryClient();

  const prefetchConversations = async (options: UseConversationsQueryOptions = {}) => {
    const {
      page = 1,
      limit = 50,
      search = '',
      status = ''
    } = options;

    await queryClient.prefetchQuery({
      queryKey: ['conversations', { page, limit, search, status }],
      queryFn: async () => {
        const filters = {
          page,
          limit,
          ...(search && { search }),
          ...(status && { status })
        };
        
        return await conversationService.getConversations(filters);
      },
      staleTime: 30 * 1000
    });
  };

  const prefetchConversation = async (conversationId: string) => {
    await queryClient.prefetchQuery({
      queryKey: ['conversation', conversationId],
      queryFn: () => conversationService.getRevolutionaryConversation(conversationId),
      staleTime: 30 * 1000
    });
  };

  return {
    prefetchConversations,
    prefetchConversation
  };
};

// Utility to invalidate conversations cache
export const useConversationsInvalidation = () => {
  const queryClient = useQueryClient();

  const invalidateConversations = () => {
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
  };

  const invalidateConversation = (conversationId: string) => {
    queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
  };

  const invalidateStats = () => {
    queryClient.invalidateQueries({ queryKey: ['conversation-stats'] });
  };

  return {
    invalidateConversations,
    invalidateConversation,
    invalidateStats
  };
}; 